"use client";
/*
 * Documentation:
 * CardEscalationSurvey — https://app.subframe.com/library?component=CardEscalationSurvey_3b1a1e28-9313-42ef-b669-149abdf1f4bb
 */

import React from "react";
import * as SubframeUtils from "../utils";
import * as SubframeCore from "@subframe/core";
import { getOrdinalSuffix } from "utils/helper";

interface CardEscalationSurveyRootProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  date?: React.ReactNode;
  escalationLevel: number;
  cause?: React.ReactNode;
  variant?:
    | "default"
    | "action-needed"
    | "in-progress"
    | "resolved"
    | "dismissed-expired";
  ticketStatus?: React.ReactNode;
  className?: string;
}

const CardEscalationSurveyRoot = React.forwardRef<
  HTMLElement,
  CardEscalationSurveyRootProps
>(function CardEscalationSurveyRoot(
  {
    title,
    date,
    escalationLevel,
    cause,
    variant = "default",
    ticketStatus,
    className,
    ...otherProps
  }: CardEscalationSurveyRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/3b1a1e28 flex w-full flex-col items-start gap-4 rounded-lg bg-white px-4 py-4",
        {
          "border border-solid border-neutral-border": variant === "resolved",
          "bg-warning-50": variant === "in-progress",
          "bg-error-50": variant === "action-needed",
        },
        className
      )}
      ref={ref as any}
      {...otherProps}
    >
      <div className="flex w-full items-center justify-between">
        {title ? (
          <span className="text-section-header font-section-header text-default-font">
            {title}
          </span>
        ) : null}
        <div
          className={SubframeUtils.twClassNames(
            "flex h-6 items-center gap-1 rounded-full bg-neutral-50 px-2",
            {
              "bg-neutral-100": variant === "dismissed-expired",
              "bg-success-50": variant === "resolved",
              "bg-warning-600": variant === "in-progress",
              "bg-error-600": variant === "action-needed",
            }
          )}
        >
          <SubframeCore.Icon
            className="hidden text-body font-body text-default-font"
            name={null}
          />
          {ticketStatus ? (
            <span
              className={SubframeUtils.twClassNames(
                "whitespace-nowrap text-label font-label text-subtext-color",
                {
                  "text-default-font": variant === "dismissed-expired",
                  "text-success-700": variant === "resolved",
                  "text-white":
                    variant === "in-progress" || variant === "action-needed",
                }
              )}
            >
              {ticketStatus}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col items-start gap-1">
        <div className="flex items-start gap-2">
          <span className="w-28 flex-none text-body-bold font-body-bold text-subtext-color">
            Escalation Date:
          </span>
          {date ? (
            <span className="text-body font-body text-default-font">
              {date}
            </span>
          ) : null}
        </div>
        <div className="flex items-start gap-2">
          <span className="w-28 flex-none text-body-bold font-body-bold text-subtext-color">
            Escalation Cause:
          </span>
          {cause ? (
            <span className="text-body font-body text-default-font">
              {cause}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export const CardEscalationSurvey = CardEscalationSurveyRoot;
